import { useEffect, useState, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canLeadTasks, isDesigner } from "@/context/AuthContext";
import { storage } from "@/lib/storage";
import { plur } from "@/lib/format";
import { Plus, LayoutGrid, List as ListIcon, CalendarDays, Users, RotateCw, CalendarClock, Briefcase, Flag, CaseSensitive, Clock } from "lucide-react";
import { toast } from "sonner";
import { useIsMobile, useIsNarrow } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useStoredView, viewEff, groupSortProp } from "@/components/shared/GroupSort";
import { InfoTip } from "@/components/settings/Grouped";
import { useListFilters } from "@/hooks/useListFilters";
import { closesWithoutApproval } from "@/lib/tasks";
import {
  ACTIVE_ST, KANBAN_COLS,
  isOverdue, isToday, isWithin7, kanbanSort, taskGroups, statusKey, taskCmp,
} from "@/components/tasks/taskUtils";
import { useTaskLists, aboveFloor } from "@/components/tasks/useTaskLists";
import { KanbanBoard } from "@/components/tasks/KanbanBoard";
import { DeadlineList, EndGroups } from "@/components/tasks/TaskRows";
import { TaskTable } from "@/components/tasks/TaskTable";
import { TaskDock } from "@/components/tasks/TaskDock";
import { useListNav, Kbd } from "@/components/shared/ListNav";
import { celebrate } from "@/lib/celebrate";
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog";
import { useTaskRowActions } from "@/components/tasks/useTaskRowActions";
import { PeopleFilterStep } from "@/components/tasks/calendar/PeopleFilter";
import { MonthView, WeekView, OverdueBar, StatusLegend, CalToolbar, ModeSwitch } from "@/components/tasks/calendar/TaskCalendar";
import { PeopleWeek } from "@/components/tasks/calendar/PeopleWeek";
import { taskItem, moveBody, hhmm, ymd, day0, addDays, mondayOf, absenceOn, parseLocal, MONTHS, MONTHS_GEN } from "@/lib/taskCalendar";

const VIEWS = ["calendar", "list", "kanban", "people"];
// 350: jednotná lišta — záložky Moje / Zadal jsem / Celá firma, Filtr Termín / Zakázka / Priorita / Kdo,
// Řadit v Seznamu, pohledy vpravo. 360: Seznam na počítači jako tabulka s detailem vpravo; dřívější záložka
// Hotové (a ještě dřív pohled Historie) = skupiny Ke schválení a Hotové na konci seznamu.
// 379: Řadit → Zobrazení (V) jako v Dokladech — Seskupit podle (i bez skupin) a Řadit podle uvnitř rozpracovaných.
const TASK_GROUPS = {
  deadline: { label: "Termín", icon: CalendarClock, dirs: ["Po termínu první", "Obráceně"] },
  priority: { label: "Priorita", icon: Flag, dirs: ["Od vysoké", "Od nízké"] },
  project: { label: "Zakázka", icon: Briefcase },
};
const TASK_ORDERS = {
  priority: { label: "Priorita", icon: Flag, dirs: ["Od nízké", "Od vysoké"], def: "desc" },
  deadline: { label: "Termín", icon: CalendarClock, dirs: ["Od nejbližšího", "Od nejpozdějšího"], def: "asc" },
  title: { label: "Název", icon: CaseSensitive, dirs: ["A → Z", "Z → A"], def: "asc" },
  created: { label: "Vytvořeno", icon: Clock, dirs: ["Nejstarší", "Nejnovější"], def: "desc" },
};
const TASK_VIEW = { g: "deadline", gd: 0, k: "priority", dir: "desc" };
const PRIOS = [{ value: "Vysoká", label: "Vysoká" }, { value: "Střední", label: "Střední" }, { value: "Nízká", label: "Nízká" }];
const DONE_PAGE = 50;   // skupina Hotové kreslí po 50 řádcích (218)
const NONE = [];

export default function Tasks() {
  const { user } = useAuth();
  const manager = canLeadTasks(user);   // vedení i projektant (173); projektantovi server vrátí jen jeho zakázky
  const mobile = useIsMobile();
  const narrow = useIsNarrow();   // mobil i tablet na výšku → Seznam s tahy
  // Tým, zakázky a výběry sdílí s dialogem Nový úkol (react-query) — dialog je má hned (218).
  const projects = useQuery({ queryKey: ["projects-options"], queryFn: () => api.get("/projects/options").then((r) => r.data) }).data || NONE;
  const team = useQuery({ queryKey: ["team"], queryFn: () => api.get("/team").then((r) => r.data), enabled: manager }).data || NONE;
  const qc = useQueryClient();
  const selections = useQuery({ queryKey: ["task-selections"], queryFn: () => api.get("/task-selections").then((r) => r.data), enabled: manager }).data || NONE;
  const setSelections = (fn) => qc.setQueryData(["task-selections"], (s) => fn(s || []));
  // Pohled se pamatuje jako u Zakázek. 191: Kalendář · Seznam · Kanban · Lidé (Lidé jen vedení);
  // 350: Historie je záložka Hotové.
  // 236: „Otevřít v Úkolech“ z Plánu na zítřek (?pohled=lide&den=YYYY-MM-DD) — Lidé na ten týden
  const urlDay = (() => { const d = new URLSearchParams(window.location.search).get("den"); return /^\d{4}-\d{2}-\d{2}$/.test(d || "") ? d : null; })();
  const [view, setViewState] = useState(() => {
    if (new URLSearchParams(window.location.search).get("pohled") === "lide") return "people";
    const v = storage.get("tasks_view");
    if (v === "history") { storage.set("tasks_view", "list"); return "list"; }   // dřívější Historie
    return VIEWS.includes(v) ? v : "calendar";
  });
  const setView = (v) => { setViewState(v); storage.set("tasks_view", v); };
  // dřívější Řadit (tasks_sort) = skupina; nové Zobrazení v tasks_order
  const [gsRaw, setGs] = useStoredView("tasks_order", TASK_GROUPS[storage.get("tasks_sort")] ? { ...TASK_VIEW, g: storage.get("tasks_sort") } : TASK_VIEW);
  const gs = viewEff(gsRaw, TASK_GROUPS, TASK_ORDERS, TASK_VIEW);
  const sortBy = gs.g;   // "deadline" | "priority" | "project" | "none"
  const orderCmp = useMemo(() => taskCmp(gs.k, gs.dir), [gs.k, gs.dir]);
  const [newOpen, setNewOpen] = useState(false);
  // V úzkém rozhraní Kanban nenabízíme; uloženou volbu „kanban" nepřepisujeme,
  // takže po otočení tabletu na šířku se uživatel vrátí tam, kde byl.
  // Dělník a subdodavatel Kanban nepotřebují — mají jen své úkoly (245).
  const kanbanOk = manager && !narrow;
  const effView = (!kanbanOk && view === "kanban") || (!manager && view === "people") ? "list" : view;
  const calView = effView === "calendar" || effView === "people";
  const [calMode, setCalModeState] = useState(() => (storage.get("tasks_cal_mode") === "week" ? "week" : "month"));
  const setCalMode = (m) => { setCalModeState(m); storage.set("tasks_cal_mode", m); };
  const [selDay, setSelDay] = useState(() => (urlDay ? day0(parseLocal(urlDay)) : day0(new Date())));
  const [cursor, setCursor] = useState(() => { const d = urlDay ? parseLocal(urlDay) : new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  // Výběr lidí (191) platí ve všech záložkách; pamatuje se v prohlížeči.
  const [peopleIds, setPeopleIdsState] = useState(() => { try { return JSON.parse(storage.get("pp-tasks-people") || "[]"); } catch { return []; } });
  const setPeopleIds = (ids) => { setPeopleIdsState(ids); storage.set("pp-tasks-people", JSON.stringify(ids)); };
  const [absences, setAbsences] = useState([]);
  const stepRef = useRef({});
  const [showAllPeople, setShowAllPeople] = useState(false);
  const [newInit, setNewInit] = useState(null);

  const [scopeRaw, setScope] = useState(() => storage.get("pp-tasks-scope") || "mine");
  const scope = scopeRaw;
  const changeScope = (v) => { setScope(v); storage.set("pp-tasks-scope", v); };

  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  useEffect(() => { const h = setTimeout(() => setQDeb(q.trim()), 300); return () => clearTimeout(h); }, [q]);
  const [searchParams] = useSearchParams();
  // Filtr Zakázka (v adrese ?project=A,B, „!“ = není) — serveru jde jen „je“ (Historie a sloupec Hotovo).
  const projRaw = searchParams.get("project") || "";
  const projF = projRaw && !projRaw.startsWith("!") ? projRaw : "all";

  useEffect(() => {
    if (!manager && scopeRaw === "all") changeScope("mine");
  }, []); // eslint-disable-line

  // 218: rozpracované celé, schválené po stránkách (filtry, které umí server) a v kalendáři okno.
  const apprScope = effView === "people" ? "all" : scope;
  const histLike = effView === "list" || effView === "kanban";   // hotové po stránkách filtruje i server
  const approvedQuery = useMemo(() => {
    const p = new URLSearchParams({ scope: apprScope });
    if (peopleIds.length) p.set("assignees", peopleIds.join(","));
    if (histLike && projF !== "all") p.set("project", projF);
    if (histLike && qDeb) p.set("q", qDeb);
    return p.toString();
  }, [apprScope, peopleIds.join(","), histLike, projF, qDeb]); // eslint-disable-line
  const weekStart = mondayOf(selDay);
  // Nepřítomnosti z Docházky (a schválené úkoly) pro zobrazený rozsah kalendáře.
  const absFrom = ymd(addDays(mondayOf(cursor < weekStart ? cursor : weekStart), -7));
  const absTo = ymd(addDays(cursor > weekStart ? cursor : weekStart, 48));
  const calWindow = useMemo(() => (calView ? { from: absFrom, to: absTo } : null), [calView, absFrom, absTo]);
  const { tasks, openTasks, loading, failed, retry, setOpenTasks, reload: load, approved, loadMoreApproved } = useTaskLists({ approvedQuery, calWindow });
  // 298: úkol založený tlačítkem Vytvořit v levém sloupci — seznam se obnoví sám
  useEffect(() => {
    window.addEventListener("pp-task-created", load);
    return () => window.removeEventListener("pp-task-created", load);
  }, [load]);

  // „Moje" = mám na starosti; „Zadal jsem" = založil jsem a řeší to někdo jiný.
  const isMine = (t) => (t.assignees || []).includes(user?.id);
  const isDelegated = (t) => t.created_by === user?.id && !isMine(t);
  // V pohledu Lidé jde vždy o celou firmu (rozsah se tam neukazuje).
  const scopeMatch = (t) => (effView === "people" ? true : scope === "mine" ? isMine(t) : scope === "delegated" ? isDelegated(t) : true);

  // Základ pro filtry a počty = po rozsahu (§2: „počty až po rozsahu"); 360: Seznam má i hotové (skupiny na konci).
  const scopeStatusBase = useMemo(() => tasks.filter(scopeMatch), [tasks, scope, effView, user?.id]); // eslint-disable-line

  const activeTeam = team.filter((u) => u.status !== "inactive" && u.role !== "ACCOUNTANT");
  // 360: pilulka Kdo ukáže název uloženého výběru (Stavbyvedoucí…), když výběr přesně sedí
  const savedSel = peopleIds.length > 1 ? selections.find((x) => x.user_ids.length === peopleIds.length && x.user_ids.every((i) => peopleIds.includes(i))) : null;
  const fGroups = useMemo(() => [
    { key: "due", label: "Termín", icon: CalendarClock,
      options: [{ value: "overdue", label: "Po termínu" }, { value: "today", label: "Dnes" }, { value: "next7", label: "Do 7 dní" }, { value: "none", label: "Bez termínu" }],
      match: (t, v) => (v === "overdue" ? isOverdue(t) : v === "today" ? isToday(t) : v === "next7" ? isWithin7(t) : !t.deadline) },
    { key: "project", label: "Zakázka", icon: Briefcase,
      options: [{ value: "none", label: "Bez zakázky" }, ...projects.map((p) => ({ value: p.id, label: p.name }))],
      match: (t, v) => (v === "none" ? !t.project_id : t.project_id === v) },
    { key: "priority", label: "Priorita", icon: Flag, options: PRIOS, match: (t, v) => (t.priority || "Střední") === v },
    // 359: Kdo se vybírá přímo ve Filtru (rychlé volby, uložené výběry, lidé po rolích), ne v samostatném okně
    ...(manager ? [{ key: "kdo", label: "Kdo", icon: Users,
      options: activeTeam.map((u) => ({ value: u.id, label: u.name })),
      external: { vals: peopleIds, labels: savedSel ? [savedSel.name] : peopleIds.map((id) => (id === user?.id ? "Já" : activeTeam.find((u) => u.id === id)?.name || "—")),
        clear: () => setPeopleIds([]),
        toggle: (id) => setPeopleIds(peopleIds.includes(id) ? peopleIds.filter((x) => x !== id) : [...peopleIds, id]) },
      render: (ctx) => <PeopleFilterStep {...stepRef.current} {...ctx} /> }] : []),
  ], [projects, manager, team, peopleIds.join(","), user?.id, savedSel]); // eslint-disable-line react-hooks/exhaustive-deps

  const F = useListFilters(scopeStatusBase, {
    groups: fGroups, searchTerm: q, entity: { one: "úkol", few: "úkoly", many: "úkolů" },
    search: (t) => `${t.title || ""} ${t.description || ""} ${t.project_name || ""} ${(t.assignee_users || []).map((a) => a.name).join(" ")}`,
  });
  const fKey = JSON.stringify(F.state);

  // Výběr lidí přepne rozsah na Celou firmu (jinak scope∧osoba = prázdno, zadání 93e).
  useEffect(() => {
    if (peopleIds.length && scopeRaw !== "all" && manager) changeScope("all");
  }, [peopleIds.join(",")]); // eslint-disable-line
  const peopleSet = new Set(peopleIds);
  const peopleKey = peopleIds.join(",");
  const visible = useMemo(() => {
    if (!peopleKey) return F.visible;
    const sel = new Set(peopleKey.split(","));
    return F.visible.filter((t) => (t.assignees || []).some((id) => sel.has(id)));
  }, [F.visible, peopleKey]);

  // 218: dokud má server starší schválené, skupina Hotové a sloupec Hotovo ukazují jen souvislý
  // načtený úsek (bez děr); další přidá „Načíst další“ / „Zobrazit starší“.
  const inLoaded = useMemo(() => aboveFloor(approved.floor), [approved.floor]);
  const kanbanTasks = useMemo(() => visible.filter((t) => t.status !== "Schváleno" || inLoaded(t)), [visible, inLoaded]);

  // ---- 360: Seznam — skupiny podle Zobrazení, na konci Ke schválení a Hotové.
  // „Zmrazená“ skupina: řádek, který se právě dokončil / schválil (nebo je otevřený v detailu), zůstane
  // na místě, dokud se detail nezavře nebo se nezmění záložka / skupiny — nic pod rukou neuskočí.
  const [frozen, setFrozen] = useState(() => new Map());
  const keyOf = (t) => frozen.get(t.id) || statusKey(t, sortBy);
  const [doneLimit, setDoneLimit] = useState(DONE_PAGE);
  useEffect(() => { setDoneLimit(DONE_PAGE); }, [approvedQuery, fKey]); // eslint-disable-line
  const [doneOpen, setDoneOpen] = useState(() => storage.get("pp-tasks-done-open") === "1");
  const [closedGroups, setClosedGroups] = useState(() => new Set());
  const isCollapsed = (k) => (k === "done" ? !doneOpen : closedGroups.has(k));
  const setDone = (v) => { setDoneOpen(v); storage.set("pp-tasks-done-open", v ? "1" : ""); };
  const toggleGroup = (k) => {
    if (k === "done") { setDone(!doneOpen); return; }
    setClosedGroups((x) => { const n = new Set(x); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  };
  const listItems = useMemo(() => visible.filter((t) => t.status !== "Schváleno" || frozen.has(t.id) || inLoaded(t)), [visible, inLoaded, frozen]);
  const doneLoaded = useMemo(() => listItems.filter((t) => keyOf(t) === "done").length, [listItems, frozen, sortBy]); // eslint-disable-line
  // počet hotových: ze serveru, dokud filtruje jen to, co umí server (rozsah, lidé, zakázka, hledání)
  const serverCount = !F.state.due && !F.state.priority && !F.state.project?.neg;
  const doneCount = approved.hasMore && approved.fresh && serverCount ? Math.max(approved.total, doneLoaded) : doneLoaded;
  const listGroups = useMemo(() => taskGroups(listItems, sortBy, keyOf, { reverse: !!gs.gd, cmp: orderCmp })
    .map((g) => (g.key === "done" ? { ...g, count: doneCount, items: g.items.slice(0, doneLimit) } : g)),
  [listItems, sortBy, gs.gd, orderCmp, frozen, doneLimit, doneCount]); // eslint-disable-line
  // Sbalit vše / Rozbalit vše ze Zobrazení (jen tabulka — na telefonu se sbalují jen Ke schválení a Hotové)
  const foldAll = (all) => { setClosedGroups(all ? new Set(listGroups.filter((g) => !g.flat && g.key !== "done").map((g) => g.key)) : new Set()); setDone(!all); };
  const doneMore = doneLoaded > doneLimit || approved.hasMore;
  const moreDone = () => {
    const next = doneLimit + DONE_PAGE;
    setDoneLimit(next);
    if (next > doneLoaded && approved.hasMore) loadMoreApproved();
  };
  const order = useMemo(() => listGroups.flatMap((g) => (isCollapsed(g.key) ? [] : g.items.map((t) => t.id))), [listGroups, closedGroups, doneOpen]); // eslint-disable-line
  const orderIdx = useMemo(() => new Map(order.map((id, i) => [id, i])), [order]);
  const calItems = useMemo(() => visible.map(taskItem).filter(Boolean), [visible]);

  // Lidé v kalendáři: vedení vidí tým (podle výběru), ostatní jen sebe.
  const shownP = manager ? (peopleIds.length ? activeTeam.filter((u) => peopleSet.has(u.id)) : activeTeam) : [{ id: user?.id, name: user?.name, role: user?.role }];
  const pById = Object.fromEntries(activeTeam.map((u) => [u.id, u]));
  useEffect(() => {
    if (!calView) return;
    api.get(`/absences?date_from=${absFrom}&date_to=${absTo}&status=approved,pending`).then((r) => setAbsences(r.data.items || [])).catch(() => setAbsences([]));
  }, [calView, absFrom, absTo]);

  // Počty v záložkách: rozpracované z načtených.
  const open = openTasks || NONE;
  const counts = useMemo(() => ({
    mine: open.filter((t) => isMine(t) && ACTIVE_ST.includes(t.status)).length,
    delegated: open.filter((t) => isDelegated(t) && ACTIVE_ST.includes(t.status)).length,
    all: open.filter((t) => ACTIVE_ST.includes(t.status)).length,
    overdue: open.filter((t) => isOverdue(t)).length,
  }), [open, user?.id]); // eslint-disable-line

  // Pořadí id pro navigaci ←/→ v modalu podle aktuálního pohledu.
  const modalIds = useMemo(() => (effView === "kanban"
    ? KANBAN_COLS.flatMap((st) => kanbanTasks.filter((t) => t.status === st).sort(kanbanSort(st))).map((t) => t.id)
    : calView ? [...calItems].sort((a, b) => a._s - b._s).map((t) => t.id)
      : order), [effView, calView, kanbanTasks, calItems, order]);

  // Optimistická úprava seznamu pro tahy Dnes/Priorita (bez blikání, zadání 96).
  const setTasks = (fn) => setOpenTasks((arr) => (arr ? fn(arr) : arr));
  const patchLocal = (id, patch) => setTasks((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  // Obsluha řádku (tahy, šuplíky, detail) sdílená s Přehledem (zadání 98/B).
  const rowActions = useTaskRowActions({ reload: load, patchLocal, navIds: modalIds });
  const { openTask, swipeProps } = rowActions;

  // Zavřít odemčený řádek při jakékoli změně filtru/pohledu.
  useEffect(() => { rowActions.setOpenId(null); }, [q, fKey, scope, view]); // eslint-disable-line

  // ---- 360: tabulka na počítači — ↑ ↓ / najetí myší výběr, mezerník otevře detail vpravo (tabulka se zúží),
  // v otevřeném detailu ↑ ↓ listují seznamem, mezerník nebo Esc zavře. Telefon a tablet na výšku beze změny.
  const tableOn = effView === "list" && !narrow;
  const [dock, setDock] = useState(null);   // { id, action } — otevřený detail vedle tabulky
  const freeze = (ids) => setFrozen((m) => { const n = new Map(m); ids.forEach((t) => { if (t && !n.has(t.id)) n.set(t.id, statusKey(t, sortBy)); }); return n; });
  const openDock = (id, action = null) => { freeze([tasks.find((t) => t.id === id)]); setDock({ id, action }); };
  const closeDock = () => { setDock(null); setFrozen(new Map()); };
  const nav = useListNav("tasks", order.length, (i) => (dock?.id === order[i] ? closeDock() : openDock(order[i])), { enabled: tableOn });
  // výběr šipkami v otevřeném detailu přepne detail; detail otevřený klikem posune výběr
  useEffect(() => { if (dock && order[nav.sel] && order[nav.sel] !== dock.id) openDock(order[nav.sel]); }, [nav.sel]); // eslint-disable-line
  useEffect(() => { if (dock) { const i = orderIdx.get(dock.id); if (i != null && i !== nav.sel) nav.setSel(i); } }, [dock?.id, orderIdx]); // eslint-disable-line
  // jiná záložka, skupiny nebo pohled = nový seznam — detail se zavře a zmrazené řádky se uvolní (jiné Řadit podle jen přeskládá)
  useEffect(() => { setDock(null); setFrozen(new Map()); }, [scope, sortBy, gs.gd, view, narrow]);
  const rowProps = (i) => { const p = nav.rowProps(i); if (dock) delete p.onMouseMove; return p; };   // s detailem myš výběr nemění
  const onRowOpen = (id) => { const i = orderIdx.get(id); if (i != null) nav.setSel(i); openDock(id); };
  const dockIdx = dock ? orderIdx.get(dock.id) : null;
  const dockNav = dockIdx != null ? { index: dockIdx, count: order.length, onGo: (i) => { nav.setSel(i); openDock(order[i]); } } : null;
  // kolečko u řádku: vlastní úkol (nebo vedení) se zavře rovnou s konfetami, jinak detail s dokončením
  const completeFromRow = (t, el) => {
    if (!rowActions.canComplete(t)) { toast.info("Dokončí řešitel."); return; }
    if (closesWithoutApproval(t, user)) {
      celebrate(el);
      freeze([t]);
      patchLocal(t.id, { status: "Schváleno", completion: { at: new Date().toISOString(), by: user.id, by_name: user.name, photos: [] } });
      api.post(`/tasks/${t.id}/complete`, { note: "", photos: [] })
        .then(() => { toast.success("Úkol hotový."); load(); })
        .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); load(); });
      return;
    }
    if (tableOn) { onRowOpen(t.id); setDock({ id: t.id, action: "complete" }); } else rowActions.completeRow(t);
  };
  // „Přidat úkol…“ ve skupině: termín / priorita / zakázka podle skupiny, řešitel já
  const quickAdd = async (key, title) => {
    const body = { title, assignees: [user.id] };
    if (sortBy === "deadline") {
      const d = day0(new Date());
      const shift = key === "week" ? 1 : key === "later" ? ((8 - d.getDay()) % 7 || 7) : 0;
      body.deadline = key === "none" ? null : ymd(addDays(d, shift));
    } else if (sortBy === "priority") body.priority = key.slice(2);
    else if (sortBy === "project" && key !== "z-none") body.project_id = key.slice(2);
    try { await api.post("/tasks", body); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doneMoreBtn = doneMore ? (
    <div className="flex justify-center p-3 border-t border-border/60">
      <button type="button" onClick={moreDone} disabled={approved.busy} data-testid="done-more"
        className="h-10 px-5 rounded-full border border-border bg-card text-sm font-bold hover:bg-secondary disabled:opacity-60 w-full md:w-auto">
        {approved.busy ? "Načítám…" : "Načíst další"}
      </button>
    </div>
  ) : null;

  // Změna pořadí ve sloupci / přesun mezi Nový↔V řešení = optimisticky (zadání 96/C):
  // nejdřív místní stav, teprve pak PATCH; při chybě se vrátí zpět. Bez load() —
  // přenačtení celého seznamu je to, co blikalo. Přijímá dávku úprav [{id, patch}],
  // ať zvládne i přečíslování sloupce, když sousedé měli shodné náhradní pořadí.
  // 218: celá dávka jedním requestem (dřív PATCH na každou kartu); když server
  // neprojde, nezapíše nic a místní stav se vrátí.
  const onReorder = (updates) => {
    const prev = openTasks;
    const map = Object.fromEntries(updates.map((u) => [u.id, u.patch]));
    setTasks((arr) => arr.map((x) => (map[x.id] ? { ...x, ...map[x.id] } : x)));
    api.post("/tasks/reorder", { items: updates.map((u) => ({ id: u.id, order: u.patch.order, ...(u.patch.status ? { status: u.patch.status } : {}) })) })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setOpenTasks(prev); });
  };

  // Přetažení po Kanbanu jen zrcadlí serverová pravidla (89/A + 91) — nevymýšlí si.
  const onMove = async (t, target) => {
    if (t.status === target) return;
    const closesNow = closesWithoutApproval(t, user);
    if (target === "Nový" || target === "V řešení") {
      // Vytažení z „Hotovo"/„Schváleno" = prosté vrácení mezi rozpracované
      // (PATCH by server na hotový úkol odmítl — proto /reopen).
      if (t.status === "Schváleno" || t.status === "Hotovo") {
        try { await api.post(`/tasks/${t.id}/reopen`, { status: target }); toast.success("Úkol vrácen mezi rozpracované."); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
        return;
      }
      try { await api.patch(`/tasks/${t.id}`, { status: target }); load(); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
      return;
    }
    if (target === "Hotovo") {   // sloupec „Ke schválení"
      // Kdo sám schvaluje, nemá co posílat ke schválení — rovnou do Hotovo.
      if (closesNow) { toast.info("Ke schválení nic posílat nemusíš — přetáhni úkol rovnou do sloupce Hotovo."); return; }
      rowActions.completeRow(t);
      return;
    }
    if (target === "Schváleno") {   // sloupec „Hotovo"
      if (t.status === "Hotovo") {   // úkol už čeká na schválení
        if (t.completion?.photos?.length) { openTask(t.id); return; }   // fotky si má vedení prohlédnout
        if (!manager) { toast.info("Schvaluje vedení — pošli úkol ke schválení."); return; }
        try { await api.post(`/tasks/${t.id}/approve`); toast.success("Úkol schválen."); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
        return;
      }
      // úkol je aktivní (Nový / V řešení) — vedení ho zavře rovnou přes /complete,
      // ať vznikne řádný záznam o dokončení (kdo a kdy), ne tichý /approve.
      if (closesNow) {
        try { await api.post(`/tasks/${t.id}/complete`, { note: "", photos: [] }); toast.success("Úkol hotový."); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
      } else {
        toast.info("Nejdřív úkol odešli ke schválení.");
      }
    }
  };

  // ---- kalendář: přeplánování tahem (termín, čas, člověk) — server hlídá práva stejně
  const canMove = (t) => !!t.deadline && t.status !== "Schváleno" && (manager || t.created_by === user?.id);
  const patchTask = async (t, body, msg) => {
    const prev = openTasks;
    patchLocal(t.id, body);
    try { await api.patch(`/tasks/${t.id}`, body); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setOpenTasks(prev); }
  };
  const onCalMove = (item, day, hour) => patchTask(item, moveBody(item, day, hour), "Úkol přeplánován.");
  const onCalResize = (item, endH) => {
    const from = item._s.getHours() + item._s.getMinutes() / 60;
    patchTask(item, { time_to: hhmm(Math.max(from + 0.25, Math.round(endH * 4) / 4)) });
  };
  const onPeopleMove = (item, day, toPid, fromPid) => {
    const body = moveBody(item, day, null);
    if (toPid !== fromPid) {
      const a = item.assignees;
      body.assignees = a.includes(toPid) ? a.filter((x) => x !== fromPid) : a.map((x) => (x === fromPid ? toPid : x));
    }
    const ab = absenceOn(absences, toPid, day);
    patchTask(item, body, ab ? `Přesunuto — pozor, ${pById[toPid]?.name || ""} má ten den nepřítomnost.` : "Úkol přeplánován.");
  };
  const navCal = (dir) => {
    if (effView === "people" || calMode === "week") { const d = addDays(selDay, 7 * dir); setSelDay(d); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }
    else { const c = new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1); setCursor(c); setSelDay(c); }
  };
  const goToday = () => { const t = day0(new Date()); setSelDay(t); setCursor(new Date(t.getFullYear(), t.getMonth(), 1)); };
  const showDay = (d) => { setSelDay(day0(d)); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); setView("calendar"); };
  const we = addDays(weekStart, 6);
  const calTitle = effView === "people" || calMode === "week"
    ? `Týden ${weekStart.getDate()}. ${weekStart.getMonth() !== we.getMonth() ? `${MONTHS_GEN[weekStart.getMonth()]} ` : ""}– ${we.getDate()}. ${MONTHS_GEN[we.getMonth()]}`
    : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const createSelection = async (name, ids) => {
    try { const r = await api.post("/task-selections", { name, user_ids: ids }); setSelections((s) => [...s, r.data]); toast.success("Výběr uložen."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteSelection = async (id) => {
    try { await api.delete(`/task-selections/${id}`); setSelections((s) => s.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const todayIso = ymd(day0(new Date()));
  const absentToday = (p) => absences.find((x) => x.user_id === p.id && x.date_from <= todayIso && x.date_to >= todayIso) || null;

  // 191: výběr lidí platí ve všech záložkách; 359: krok Filtr › Kdo (čte se přes ref, ať má vždy čerstvé výběry)
  stepRef.current = { people: activeTeam, value: peopleIds, onChange: setPeopleIds, selections, meId: user?.id,
    onCreateSelection: createSelection, onDeleteSelection: deleteSelection, absentToday,
    applyLabel: `Zobrazit ${visible.length} ${F.plural(visible.length)}` };

  const addTrigger = (
    <NewTaskDialog onCreated={load} trigger={
      <button data-testid="kanban-add-task" className="w-full h-10 rounded-[12px] border border-dashed border-border text-sm font-bold text-muted-foreground hover:border-brand hover:text-brand transition-colors flex items-center justify-center gap-1.5"><Plus size={16} /> Přidat úkol</button>
    } />
  );

  const views = [
    { value: "calendar", label: "Kalendář", icon: CalendarDays, testid: "view-tab-calendar" },
    { value: "list", label: "Seznam", icon: ListIcon, testid: "view-tab-list" },
    ...(kanbanOk ? [{ value: "kanban", label: "Kanban", icon: LayoutGrid, testid: "view-tab-kanban" }] : []),
    ...(manager ? [{ value: "people", label: "Lidé", icon: Users, testid: "view-tab-people" }] : []),
  ];
  const tabs = [
    { value: "mine", label: "Moje", count: counts.mine },
    { value: "delegated", label: "Zadal jsem", count: counts.delegated },
    // Cizí úkoly vidí jen vedení — dělníkovi by „Celá firma" byla kopie dvou záložek výš.
    ...(manager ? [{ value: "all", label: isDesigner(user) ? "Moje zakázky" : "Celá firma", count: counts.all }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="tasks-page">
      {/* Úkol smí zadat kdokoli — dělník např. požadavek na vedení („objednat materiál"). */}
      <ListHeader title="Úkoly" mobile={mobile} testid="tasks-header"
        summary={openTasks ? (
          <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="tasks-summary">
            {plur(counts.all, "aktivní", "aktivní", "aktivních")} · <span className={counts.overdue ? "text-destructive font-semibold" : ""}>{counts.overdue} po termínu</span>
            {narrow && effView === "list" && <InfoTip label="Jak na tahy" text="Tahni doprava → hotovo · doleva → akce · přidrž → smazat" />}
            {calView && <InfoTip label="Barvy v kalendáři" text={<StatusLegend />} />}
          </span>
        ) : null}
        search={q} onSearch={setQ} searchPh="Hledat úkol, zakázku, člověka…"
        primary={{ label: "Úkol", full: "Nový úkol", onClick: () => setNewOpen(true), testid: "new-task-btn" }} />

      <ListToolbar mobile={mobile} testid="tasks-toolbar"
        tabs={effView === "people" ? undefined : tabs} tab={scopeRaw} onTab={changeScope}
        filter={F} search={q} onSearch={setQ} searchPh="Hledat úkol, zakázku, člověka…"
        sort={effView === "list" ? groupSortProp({ groups: TASK_GROUPS, orders: TASK_ORDERS, view: gs, setView: setGs, def: TASK_VIEW, onFold: tableOn ? foldAll : undefined }) : undefined}
        views={views} view={effView} onView={setView} />
      <NewTaskDialog hideTrigger open={newOpen} onOpenChange={setNewOpen} onCreated={load} />

      {/* 218: první načtení a výpadek — ne „nic tu není“ */}
      {failed && !openTasks && (
        <div className="bg-card border border-border rounded-[16px] p-5 md:p-6 flex flex-col sm:flex-row items-center gap-3 sm:justify-between" data-testid="tasks-error">
          <p className="text-sm text-muted-foreground text-center sm:text-left">Úkoly se nepodařilo načíst. Zkontroluj připojení a zkus to znovu.</p>
          <button type="button" onClick={retry} data-testid="tasks-retry"
            className="h-10 px-5 rounded-full border border-border bg-card text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-secondary w-full sm:w-auto shrink-0">
            <RotateCw size={15} /> Zkusit znovu
          </button>
        </div>
      )}
      {loading && !calView && (
        <div className="space-y-2" data-testid="tasks-loading" aria-busy="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-[74px] rounded-[16px] bg-secondary animate-pulse motion-reduce:animate-none" />)}
        </div>
      )}

      {/* Kalendář a Lidé (191) */}
      {calView && (
        <div className="space-y-3">
          <OverdueBar items={calItems} pById={pById} onOpen={openTask} onShow={showDay} />
          <CalToolbar title={calTitle} onPrev={() => navCal(-1)} onToday={goToday} onNext={() => navCal(1)}>
            {effView === "calendar" && <ModeSwitch value={calMode} onChange={setCalMode} options={[["month", "Měsíc"], ["week", "Týden"]]} />}
          </CalToolbar>
          {effView === "calendar" && calMode === "month" && (
            <MonthView cursor={cursor} selDay={selDay} setSelDay={setSelDay} items={calItems} shownP={shownP} absences={absences}
              onOpen={openTask} onNew={setNewInit} onMove={onCalMove} onResize={onCalResize} canMove={canMove} narrow={narrow} />
          )}
          {effView === "calendar" && calMode === "week" && (
            <WeekView weekStart={weekStart} items={calItems} shownP={shownP} absences={absences}
              onOpen={openTask} onNew={setNewInit} onMove={onCalMove} onResize={onCalResize} canMove={canMove} narrow={narrow} />
          )}
          {effView === "people" && (
            <PeopleWeek weekStart={weekStart} items={calItems} people={shownP} absences={absences} onOpen={openTask} onNew={setNewInit}
              onMove={onPeopleMove} canMove={canMove} showAll={showAllPeople} setShowAll={setShowAllPeople}
              onShowOnly={(ids) => { setPeopleIds(ids); setShowAllPeople(true); }} narrow={narrow} />
          )}
          {!calItems.length && openTasks && effView === "calendar" && <p className="text-[13px] text-muted-foreground text-center">V kalendáři jsou úkoly s termínem — úkoly bez termínu najdeš v Seznamu.</p>}
        </div>
      )}

      {/* Formulář otevřený z kalendáře / Lidí (den, čas, člověk předvyplněné) */}
      <NewTaskDialog hideTrigger open={!!newInit} onOpenChange={(o) => { if (!o) setNewInit(null); }} initial={newInit || undefined} onCreated={load} />

      {/* Obsah dle pohledu */}
      {effView === "kanban" && openTasks && (
        kanbanTasks.length === 0
          ? <p className="text-muted-foreground py-8 text-center">Žádné úkoly.</p>
          : <KanbanBoard tasks={kanbanTasks} onMove={onMove} onReorder={onReorder} onOpen={openTask} isDelegated={isDelegated} addTrigger={addTrigger} lead={manager}
              doneHasMore={approved.hasMore} onLoadMoreDone={loadMoreApproved}
              doneTotal={approved.fresh && !F.filterCount && !q.trim() ? approved.total : null} />
      )}

      {effView === "list" && openTasks && (listItems.length === 0
        ? <p className="text-muted-foreground py-8 text-center" data-testid="tasks-empty">
            {F.filterCount || q.trim() ? "Filtru nic neodpovídá." : scope === "delegated" ? "Nikomu jste zatím nic nezadal." : scope === "mine" ? "Nemáte žádné úkoly." : "Žádné úkoly."}
          </p>
        : tableOn ? (
          <div className="flex items-start gap-4" data-testid="tasks-split">
            <div className="flex-1 min-w-0 space-y-2">
              <TaskTable groups={listGroups} compact={!!dock} index={orderIdx} sel={nav.sel} openId={dock?.id} rowProps={rowProps}
                onOpen={onRowOpen} onComplete={completeFromRow} canComplete={rowActions.canComplete} isDelegated={isDelegated}
                collapsed={isCollapsed} onToggle={toggleGroup} onAdd={quickAdd} more={doneMoreBtn} />
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-muted-foreground" data-testid="tasks-keys">
                <span className="inline-flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> vybrat</span>
                <span className="inline-flex items-center gap-1"><Kbd>mezerník</Kbd> otevřít a zavřít detail</span>
              </p>
            </div>
            {dock && <TaskDock taskId={dock.id} nav={dockNav} onClose={closeDock} onChanged={load} initialAction={dock.action} />}
          </div>
        ) : (
          <div className="space-y-4">
            <DeadlineList groups={listGroups.filter((g) => !g.end)} mobile onOpen={openTask} onComplete={rowActions.completeRow}
              canComplete={rowActions.canComplete} isDelegated={isDelegated} swipe={swipeProps} />
            <EndGroups groups={listGroups.filter((g) => g.end)} collapsed={isCollapsed} onToggle={toggleGroup} isDelegated={isDelegated}
              onOpen={openTask} onReopen={rowActions.reopenRow} canReopen={rowActions.canReopen} canDelete={rowActions.canDelete}
              onDelete={swipeProps.onDelete} openId={swipeProps.openId} setOpenId={swipeProps.setOpenId} more={doneMoreBtn} />
          </div>
        )
      )}

      {rowActions.sheets}
    </div>
  );
}
